import React from 'react';
import { ArrowLeftIcon, CreditCardIcon, CalendarIcon, DollarSignIcon, BuildingIcon, FileTextIcon, UserIcon, ClockIcon, ServerIcon, MessageSquareIcon, CheckIcon, AlertTriangleIcon, XIcon } from 'lucide-react';
import { Payment } from './PaymentsView';
import { formatCurrency } from '../../utils/currencyFormatter';
interface PaymentDetailProps {
  payment: Payment;
  onBack: () => void;
  locale: string;
}
export function PaymentDetail({
  payment,
  onBack,
  locale
}: PaymentDetailProps) {
  // Format dates for display
  const formattedPaymentDate = new Date(payment.paymentDate).toLocaleDateString(locale);
  const formattedCreatedAt = new Date(payment.createdAt).toLocaleString(locale);
  // Calculate total invoice amount
  const totalInvoiceAmount = payment.linkedInvoices.reduce((sum, invoice) => sum + invoice.invoiceTotal, 0);
  // Calculate total amount paid
  const totalAmountPaid = payment.linkedInvoices.reduce((sum, invoice) => sum + invoice.amountPaid, 0);
  // Check if this is a multi-vendor payment
  const uniqueVendors = new Set(payment.linkedInvoices.map(inv => inv.vendorId));
  const isMultiVendor = uniqueVendors.size > 1;
  // Get status icon
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Cleared':
        return <CheckIcon className="h-4 w-4 text-green-500" data-id="element-10507" />;
      case 'Pending':
        return <ClockIcon className="h-4 w-4 text-yellow-500" data-id="element-10508" />;
      case 'Failed':
        return <XIcon className="h-4 w-4 text-red-500" data-id="element-10509" />;
      case 'Cancelled':
        return <AlertTriangleIcon className="h-4 w-4 text-gray-500" data-id="element-10510" />;
      default:
        return null;
    }
  };
  return <div className="bg-white shadow rounded-lg" data-id="element-10511">
      {/* Header */}
      <div className="px-4 py-5 border-b border-gray-200 sm:px-6 flex justify-between items-center" data-id="element-10512">
        <div className="flex items-center" data-id="element-10513">
          <button onClick={onBack} className="mr-4 inline-flex items-center p-2 border border-transparent rounded-full shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-10514">
            <ArrowLeftIcon className="h-4 w-4" data-id="element-10515" />
          </button>
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-10516">
            Payment Details
          </h3>
        </div>
        <span className={`px-3 py-1 inline-flex text-sm leading-5 font-semibold rounded-full ${payment.status === 'Cleared' ? 'bg-green-100 text-green-800' : payment.status === 'Pending' ? 'bg-yellow-100 text-yellow-800' : payment.status === 'Failed' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-10517">
          {payment.status}
        </span>
      </div>

      <div className="px-4 py-5 sm:px-6" data-id="element-10518">
        <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2 lg:grid-cols-3" data-id="element-10519">
          {/* Payment Header Info Section */}
          <div className="sm:col-span-3" data-id="element-10520">
            <h4 className="text-md font-medium text-gray-900 mb-4 flex items-center" data-id="element-10521">
              <CreditCardIcon className="h-5 w-5 mr-2 text-indigo-500" data-id="element-10522" />
              Payment Information
            </h4>
          </div>

          <div className="sm:col-span-1" data-id="element-10523">
            <dt className="text-sm font-medium text-gray-500" data-id="element-10524">
              Payment Reference
            </dt>
            <dd className="mt-1 text-sm text-gray-900" data-id="element-10525">
              {payment.paymentReference}
            </dd>
          </div>

          <div className="sm:col-span-1" data-id="element-10526">
            <dt className="text-sm font-medium text-gray-500" data-id="element-10527">Payment Date</dt>
            <dd className="mt-1 text-sm text-gray-900 flex items-center" data-id="element-10528">
              <CalendarIcon className="h-4 w-4 mr-1 text-gray-400" data-id="element-10529" />
              {formattedPaymentDate}
            </dd>
          </div>

          <div className="sm:col-span-1" data-id="element-10530">
            <dt className="text-sm font-medium text-gray-500" data-id="element-10531">
              Payment Method
            </dt>
            <dd className="mt-1 text-sm text-gray-900" data-id="element-10532">{payment.method}</dd>
          </div>

          <div className="sm:col-span-1" data-id="element-10533">
            <dt className="text-sm font-medium text-gray-500" data-id="element-10534">
              Payment Amount
            </dt>
            <dd className="mt-1 text-sm text-gray-900 font-semibold flex items-center" data-id="element-10535">
              <DollarSignIcon className="h-4 w-4 mr-1 text-gray-400" data-id="element-10536" />
              {formatCurrency(payment.amount, payment.currency, locale)}
            </dd>
          </div>

          <div className="sm:col-span-1" data-id="element-10537">
            <dt className="text-sm font-medium text-gray-500" data-id="element-10538">Currency</dt>
            <dd className="mt-1 text-sm text-gray-900" data-id="element-10539">{payment.currency}</dd>
          </div>

          {payment.externalSourceId && <div className="sm:col-span-1" data-id="element-10540">
              <dt className="text-sm font-medium text-gray-500" data-id="element-10541">
                External Source ID
              </dt>
              <dd className="mt-1 text-sm text-gray-900" data-id="element-10542">
                {payment.externalSourceId}
              </dd>
            </div>}

          {/* Linked Invoices Section */}
          <div className="sm:col-span-3 border-t pt-6 mt-2" data-id="element-10543">
            <h4 className="text-md font-medium text-gray-900 mb-4 flex items-center" data-id="element-10544">
              <FileTextIcon className="h-5 w-5 mr-2 text-indigo-500" data-id="element-10545" />
              Linked Invoices ({payment.linkedInvoices.length})
            </h4>
          </div>

          <div className="sm:col-span-3" data-id="element-10546">
            <div className="overflow-x-auto" data-id="element-10547">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-10548">
                <thead className="bg-gray-50" data-id="element-10549">
                  <tr data-id="element-10550">
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10551">
                      Invoice Number
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10552">
                      Vendor
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10553">
                      Invoice Date
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10554">
                      Invoice Total
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10555">
                      Amount Paid
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10556">
                      Remaining Balance
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-10557">
                  {payment.linkedInvoices.map(invoice => <tr key={invoice.invoiceId} className="hover:bg-gray-50" data-id="element-10558">
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-indigo-600" data-id="element-10559">
                        {invoice.invoiceNumber}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900" data-id="element-10560">
                        {invoice.vendorName}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900" data-id="element-10561">
                        {new Date(invoice.invoiceDate).toLocaleDateString(locale)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900" data-id="element-10562">
                        {formatCurrency(invoice.invoiceTotal, payment.currency, locale)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900" data-id="element-10563">
                        {formatCurrency(invoice.amountPaid, payment.currency, locale)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium" data-id="element-10564">
                        <span className={invoice.remainingBalance === 0 ? 'text-green-600' : 'text-yellow-600'} data-id="element-10565">
                          {formatCurrency(invoice.remainingBalance || 0, payment.currency, locale)}
                          {invoice.remainingBalance === 0 && <CheckIcon className="inline-block h-4 w-4 ml-1 text-green-500" data-id="element-10566" />}
                        </span>
                      </td>
                    </tr>)}
                  {/* Summary row */}
                  <tr className="bg-gray-50 font-medium" data-id="element-10567">
                    <td colSpan={3} className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-right" data-id="element-10568">
                      Totals:
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900" data-id="element-10569">
                      {formatCurrency(totalInvoiceAmount, payment.currency, locale)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900" data-id="element-10570">
                      {formatCurrency(totalAmountPaid, payment.currency, locale)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900" data-id="element-10571">
                      {formatCurrency(totalInvoiceAmount - totalAmountPaid, payment.currency, locale)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {isMultiVendor && <div className="mt-2 bg-yellow-50 p-2 rounded-md text-sm text-yellow-700 flex items-center" data-id="element-10572">
                <AlertTriangleIcon className="h-4 w-4 mr-2" data-id="element-10573" />
                This payment is allocated across multiple vendors
              </div>}

            {payment.status === 'Cleared' && totalAmountPaid < totalInvoiceAmount && <div className="mt-2 bg-blue-50 p-2 rounded-md text-sm text-blue-700 flex items-center" data-id="element-10574">
                  <InfoIcon className="h-4 w-4 mr-2" data-id="element-10575" />
                  This payment partially covers the linked invoices. Additional
                  payments will be needed to clear the remaining balance.
                </div>}
          </div>

          {/* Metadata Section */}
          <div className="sm:col-span-3 border-t pt-6 mt-2" data-id="element-10576">
            <h4 className="text-md font-medium text-gray-900 mb-4 flex items-center" data-id="element-10577">
              <BuildingIcon className="h-5 w-5 mr-2 text-indigo-500" data-id="element-10578" />
              Metadata
            </h4>
          </div>

          <div className="sm:col-span-1" data-id="element-10579">
            <dt className="text-sm font-medium text-gray-500" data-id="element-10580">
              Organisation Unit
            </dt>
            <dd className="mt-1 text-sm text-gray-900" data-id="element-10581">
              {payment.orgUnitName}
            </dd>
          </div>

          <div className="sm:col-span-2" data-id="element-10582">
            <dt className="text-sm font-medium text-gray-500" data-id="element-10583">
              Organisation Path
            </dt>
            <dd className="mt-1 text-sm text-gray-500" data-id="element-10584">
              {payment.orgUnitPath}
            </dd>
          </div>

          <div className="sm:col-span-1" data-id="element-10585">
            <dt className="text-sm font-medium text-gray-500" data-id="element-10586">Created At</dt>
            <dd className="mt-1 text-sm text-gray-900 flex items-center" data-id="element-10587">
              <ClockIcon className="h-4 w-4 mr-1 text-gray-400" data-id="element-10588" />
              {formattedCreatedAt}
            </dd>
          </div>

          <div className="sm:col-span-1" data-id="element-10589">
            <dt className="text-sm font-medium text-gray-500" data-id="element-10590">Source System</dt>
            <dd className="mt-1 text-sm text-gray-900 flex items-center" data-id="element-10591">
              <ServerIcon className="h-4 w-4 mr-1 text-gray-400" data-id="element-10592" />
              {payment.sourceSystem}
            </dd>
          </div>

          {/* Notes Section (if available) */}
          {payment.notes && <>
              <div className="sm:col-span-3 border-t pt-6 mt-2" data-id="element-10593">
                <h4 className="text-md font-medium text-gray-900 mb-4 flex items-center" data-id="element-10594">
                  <MessageSquareIcon className="h-5 w-5 mr-2 text-indigo-500" data-id="element-10595" />
                  Notes
                </h4>
              </div>
              <div className="sm:col-span-3" data-id="element-10596">
                <dd className="mt-1 text-sm text-gray-900 bg-gray-50 p-3 rounded-md" data-id="element-10597">
                  {payment.notes}
                </dd>
              </div>
            </>}
        </dl>
      </div>
    </div>;
}
// For the InfoIcon in the component
function InfoIcon(props) {
  return <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-10598">
      <circle cx="12" cy="12" r="10" data-id="element-10599" />
      <line x1="12" y1="16" x2="12" y2="12" data-id="element-10600" />
      <line x1="12" y1="8" x2="12.01" y2="8" data-id="element-10601" />
    </svg>;
}